import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LicenseInfo } from "@mui/x-license-pro";
import { SnackbarProvider } from "./contexts/SnackbarContext";
import { FreyrLibraryProvider } from "./context/FreyrLibraryContext";
import { BrandingProvider } from "./branding";
import { getApiBaseUrl, getHostApiBaseUrl } from "./config/env";
import {
  getAuthorizationHeader,
  getDomain,
  getTenantId,
} from "./services/apiClient";
import AiPlanPage from "./pages/AiPlanPage";
import AiHubPage from "./pages/AiHubPage";
import TaggingResultDetailPage from "./pages/AiHubPage/TaggingResultDetailPage";
import ConditionalTaggingResultDetailPage from "./pages/AiHubPage/ConditionalTaggingResultDetailPage";
import {
  AI_PLAN_ADD_ENHANCED_TAGGING_RULE_PATH,
  AI_PLAN_ADD_ITEMS_PATH,
  AI_PLAN_ADD_OTO_PLAN_PATH,
  AI_PLAN_ADD_TRIAGE_RULE_PATH,
  AI_PLAN_EDIT_ENHANCED_TAGGING_RULE_PATH,
  AI_PLAN_EDIT_OTO_PLAN_PATH,
  AI_PLAN_EDIT_TRIAGE_RULE_PATH,
  AI_PLAN_PATH,
} from "./pages/AiPlanPage/constants";
import {
  AI_HUB_ASSESSMENT_PACK_PATH,
  AI_HUB_CONDITIONAL_TAGGING_RESULT_PATH,
  AI_HUB_OBJECT_ANALYSIS_PATH,
  AI_HUB_PATH,
  AI_HUB_TAGGING_RESULT_PATH,
  AI_HUB_TRIAGE_RESULT_PATH,
} from "./pages/AiHubPage/constants";
import ObjectAnalysisDetailPage from "./pages/AiHubPage/ObjectAnalysis/ObjectAnalysisDetailPage";
import TriageResultDetailPage from "./pages/AiHubPage/TriageResult/TriageResultDetailPage";
import AssessmentPackDetailPage from "./pages/AiHubPage/ImplementationRecords/AssessmentPackDetailPage";
import AddAssessmentPlanItemsPage from "./pages/AiPlanPage/TaggingPlan/AddAssessmentPlanItemsPage";
import AddEnhancedTaggingRulePage from "./pages/AiPlanPage/EnhancedTaggingRule/AddEnhancedTaggingRulePage";
import AddEditObjectToObjectPlanPage from "./pages/AiPlanPage/ObjectToObjectPlan/AddEditObjectToObjectPlanPage";
import AddTriageRulePage from "./pages/AiPlanPage/TriagePlan/AddTriageRulePage";
import { useUserPrivileges } from "./hooks/useUserPrivileges";
import { useGlobalConfig } from "./hooks/useGlobalConfig";
import { useModuleDisplayName } from "./hooks/useModuleDisplayName";
import "./index.css";

/**
 * Mounts once under <BrowserRouter> so that hitting any route directly
 * still triggers one-time session fetches into shared caches/store:
 * - GET /authz/privileges
 * - GET /admin-flags
 * - GET /global/config (module_display_name → document.title)
 */
function SessionBootstrap({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  useUserPrivileges();
  useGlobalConfig();
  useModuleDisplayName();
  return <>{children}</>;
}

const queryClient = new QueryClient();
type BrowserProcessShim = {
  env: Record<string, string | undefined>;
};

const globalWithProcess = globalThis as typeof globalThis & {
  process?: BrowserProcessShim;
};
const browserProcess = globalWithProcess.process ?? { env: {} };
const commonPackageApiPrefix = getApiBaseUrl().replace(/\/$/, "");
const hostApiBaseUrl = getHostApiBaseUrl().replace(/\/$/, "");
browserProcess.env = {
  ...browserProcess.env,
  NODE_ENV: import.meta.env.MODE,
  IM_PREFIX: commonPackageApiPrefix,
  REACT_APP_API_GETWAY: hostApiBaseUrl,
};
globalWithProcess.process = browserProcess;

const muiLicenseKey =
  import.meta.env.VITE_MUI_LICENSE_KEY ??
  import.meta.env.VITE_REACT_APP_MUI_LICENSE_KEY ??
  "973425b63aaa040dd062fbfab76187cfTz0xMjE5ODMsRT0xNzk1MDQ2Mzk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y";

if (muiLicenseKey) {
  LicenseInfo.setLicenseKey(muiLicenseKey);
}

const container = document.getElementById("root");
if (!container) {
  throw new Error("#root container not found");
}

/**
 * Vite/@vitejs/plugin-react will re-execute modules on HMR. Using a singleton
 * root avoids calling `createRoot()` twice on the same DOM node.
 */
const win = globalThis as typeof globalThis & {
  __AIHUB_REACT_ROOT__?: ReturnType<typeof ReactDOM.createRoot>;
};
const root = win.__AIHUB_REACT_ROOT__ ?? ReactDOM.createRoot(container);
win.__AIHUB_REACT_ROOT__ = root;

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrandingProvider>
        <FreyrLibraryProvider
          language={navigator.language || "en"}
          token={getAuthorizationHeader()}
          tenant={getTenantId()}
          domain={getDomain()}
          domainName={getDomain()}
          envVars={{
            IM_PREFIX: commonPackageApiPrefix,
            URF_PREFIX: hostApiBaseUrl,
          }}
        >
          <SnackbarProvider>
            <BrowserRouter>
              <SessionBootstrap>
                <Routes>
                  <Route
                    path="/"
                    element={<Navigate to={AI_HUB_PATH} replace />}
                  />
                  <Route path={AI_PLAN_PATH} element={<AiPlanPage />} />
                  <Route
                    path={AI_PLAN_ADD_ITEMS_PATH}
                    element={<AddAssessmentPlanItemsPage />}
                  />
                  <Route
                    path={AI_PLAN_ADD_ENHANCED_TAGGING_RULE_PATH}
                    element={<AddEnhancedTaggingRulePage />}
                  />
                  <Route
                    path={AI_PLAN_EDIT_ENHANCED_TAGGING_RULE_PATH}
                    element={<AddEnhancedTaggingRulePage />}
                  />
                  <Route
                    path={AI_PLAN_ADD_TRIAGE_RULE_PATH}
                    element={<AddTriageRulePage />}
                  />
                  <Route
                    path={AI_PLAN_EDIT_TRIAGE_RULE_PATH}
                    element={<AddTriageRulePage />}
                  />
                  <Route
                    path={AI_PLAN_ADD_OTO_PLAN_PATH}
                    element={<AddEditObjectToObjectPlanPage />}
                  />
                  <Route
                    path={AI_PLAN_EDIT_OTO_PLAN_PATH}
                    element={<AddEditObjectToObjectPlanPage />}
                  />
                  <Route path={AI_HUB_PATH} element={<AiHubPage />} />
                  <Route
                    path={AI_HUB_TAGGING_RESULT_PATH}
                    element={<TaggingResultDetailPage />}
                  />
                  <Route
                    path={AI_HUB_CONDITIONAL_TAGGING_RESULT_PATH}
                    element={<ConditionalTaggingResultDetailPage />}
                  />
                  <Route
                    path={AI_HUB_OBJECT_ANALYSIS_PATH}
                    element={<ObjectAnalysisDetailPage />}
                  />
                  <Route
                    path={AI_HUB_TRIAGE_RESULT_PATH}
                    element={<TriageResultDetailPage />}
                  />
                  <Route
                    path={AI_HUB_ASSESSMENT_PACK_PATH}
                    element={<AssessmentPackDetailPage />}
                  />
                </Routes>
              </SessionBootstrap>
            </BrowserRouter>
          </SnackbarProvider>
        </FreyrLibraryProvider>
      </BrandingProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
